import {useEffect, useState} from "react";
import {OrganizationFA} from "../interfaces/global-interfaces";
import {deleteOrganization, get_user_by_id, remove_user} from "../services/apiService";
import { useNavigate } from "react-router-dom";
import {UserPlus, Users, Trash2, Building2, Pencil, AlertTriangle} from "lucide-react";
import {Card} from "../components/ui/Card";
import {Button} from "../components/ui/Button";
import {EmptyState} from "../components/ui/EmptyState";
import {Loader} from "../components/ui/Loader";
import {ConfirmModal} from "../components/ConfirmModal";
import {EditOrganizationModal} from "../components/EditOrganizationModal";
import {useToast} from "../components/ui/Toast";
import {staggerDelay} from "../utils/animation";
import {getErrorMessage} from "../utils/errors";
import {useOrganization} from "../context/OrganizationContext";

interface OrganizationDetailsProps {
    organization: OrganizationFA;
}

export const OrganizationPage = ({organization}: OrganizationDetailsProps) => {
    const [users, setUsers] = useState<{
        email: string;
        id: string;
        username: string;
        surname:string
    }[]>([]);
    const [loading, setLoading] = useState(true);
    const [userToDelete, setUserToDelete] = useState<{ id: string; username: string } | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOrgOpen, setIsDeleteOrgOpen] = useState(false);
    const [deletingOrg, setDeletingOrg] = useState(false);
    const {showToast} = useToast();
    const {organizations, setOrganizations, setSelectedOrganizationId} = useOrganization();

    useEffect(() => {
        const fetchUsers = async () => {
            if (!organization.users || organization.users.length === 0) {
                setUsers([]);
                setLoading(false);
                return;
            }

            try {
                const usersData = await Promise.all(
                    organization.users.map(async (userId) => {
                        const user = await get_user_by_id(userId);
                        return {id: userId, username: user.user_name, email: user.email, surname: user.user_surname};
                    })
                );
                setUsers(usersData);
            } catch (error) {
                console.error("Error fetching users:", error);
                showToast("Couldn't load the members of this organization.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [organization.users, showToast]);

    const navigate = useNavigate();

    const handleAddUser = () => {
        navigate("/organization/add-user");
    };

    const handleConfirmDeleteUser = async () => {
        if (!userToDelete) return;
        setDeleting(true);
        try {
            await remove_user(organization._id, userToDelete.id);
            setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id));
            showToast(`${userToDelete.username} was removed from the organization.`, "success");
        } catch (error) {
            console.error("Error deleting user:", error);
            showToast("Couldn't remove this user. Please try again.", "error");
        } finally {
            setDeleting(false);
            setUserToDelete(null);
        }
    };

    const handleConfirmDeleteOrganization = async () => {
        setDeletingOrg(true);
        try {
            await deleteOrganization(organization._id);
            const remaining = organizations.filter(o => o._id !== organization._id);
            setOrganizations(remaining);
            setSelectedOrganizationId(remaining[0]?._id ?? "");
            showToast(`"${organization.name}" was deleted.`, "success");
            navigate("/dashboard");
        } catch (error) {
            console.error("Error deleting organization:", error);
            showToast(getErrorMessage(error, "Couldn't delete this organization. Please try again."), "error");
        } finally {
            setDeletingOrg(false);
            setIsDeleteOrgOpen(false);
        }
    };

    return (
        <div className="w-full h-full overflow-y-auto pr-1">
            <ConfirmModal
                isOpen={!!userToDelete}
                title="Remove user"
                message={`Are you sure you want to remove ${userToDelete?.username} from this organization?`}
                showDeleteButton
                onConfirm={handleConfirmDeleteUser}
                onCancel={() => !deleting && setUserToDelete(null)}
            />

            <ConfirmModal
                isOpen={isDeleteOrgOpen}
                title="Delete organization"
                message={`Are you sure you want to delete "${organization.name}"? This will permanently delete all of its projects, forms and submissions. This action cannot be undone.`}
                showDeleteButton
                onConfirm={handleConfirmDeleteOrganization}
                onCancel={() => !deletingOrg && setIsDeleteOrgOpen(false)}
            />

            <EditOrganizationModal
                isOpen={isEditOpen}
                organization={organization}
                onClose={() => setIsEditOpen(false)}
                onSaved={(updated) => {
                    setOrganizations(prev => prev.map(o => o._id === updated._id ? updated : o));
                }}
            />

            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="size-11 rounded-xl bg-primary-tint text-primary flex items-center justify-center shrink-0">
                        <Building2 size={20}/>
                    </div>
                    <h1 className="text-2xl font-display font-bold text-ink truncate">{organization.name}</h1>
                    <button
                        onClick={() => setIsEditOpen(true)}
                        aria-label="Edit organization"
                        title="Edit organization"
                        className="size-8 shrink-0 flex items-center justify-center rounded-full text-ink-faint hover:text-primary hover:bg-surface-hover transition-colors"
                    >
                        <Pencil size={14}/>
                    </button>
                </div>

                <Button icon={UserPlus} onClick={handleAddUser}>
                    Add User
                </Button>
            </div>
            <p className="text-sm text-ink-muted mb-8 max-w-2xl">{organization.description}</p>

            <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-ink-muted"/>
                <h2 className="text-base font-display font-bold text-ink">Users</h2>
            </div>

            {loading ? (
                <Loader/>
            ) : users.length > 0 ? (
                <Card padding="none">
                    <ul className="divide-y divide-border">
                        {users.map((user, i) => (
                            <li
                                key={user.id}
                                className="p-4 flex justify-between items-center gap-4 hover:bg-surface-hover transition-colors first:rounded-t-2xl last:rounded-b-2xl animate-fade-in-up"
                                style={staggerDelay(i)}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="size-9 shrink-0 rounded-full bg-surface-sunken flex items-center justify-center text-xs font-bold text-ink-muted">
                                        {user.username?.[0]?.toUpperCase() ?? "?"}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-ink truncate">
                                            {user.username}{user.surname ? ` ${user.surname}` : ''}
                                        </p>
                                        <p className="text-xs text-ink-muted truncate">{user.email}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="danger-ghost"
                                    size="sm"
                                    icon={Trash2}
                                    onClick={() => setUserToDelete({id: user.id, username: user.username})}
                                >
                                    Remove
                                </Button>
                            </li>
                        ))}
                    </ul>
                </Card>
            ) : (
                <Card>
                    <EmptyState
                        icon={Users}
                        title="No users in this organization"
                        description="Add teammates so they can collaborate on projects and forms."
                        action={<Button icon={UserPlus} size="sm" onClick={handleAddUser}>Add User</Button>}
                    />
                </Card>
            )}

            <div className="mt-10 pt-6 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} className="text-error"/>
                    <h3 className="text-sm font-semibold text-error">Danger Zone</h3>
                </div>
                <p className="text-sm text-ink-muted mb-3 max-w-2xl">
                    Permanently deletes {organization.name} along with all of its projects, forms and submissions. This action cannot be undone.
                </p>
                <Button
                    variant="danger"
                    size="sm"
                    icon={Trash2}
                    onClick={() => setIsDeleteOrgOpen(true)}
                >
                    Delete Organization
                </Button>
            </div>
        </div>
    );
};
